/**
 * The single place the THKY platform location is resolved.
 *
 * The CTA target is configured entirely through `VITE_PLATFORM_APP_URL`:
 *   - development : a local route (e.g. http://localhost:5173)
 *   - deployment  : the ATHKYA platform subdomain, injected at build time
 *
 * No domain is hardcoded anywhere in this codebase. If the variable is absent
 * we fall back to a relative path rather than inventing a host, so a
 * misconfigured build degrades visibly instead of silently pointing somewhere
 * wrong.
 */

const RAW = import.meta.env.VITE_PLATFORM_APP_URL?.trim();

/** True when the deployment has been given a platform location. */
export const isPlatformConfigured = Boolean(RAW);

function base(): string {
  if (!RAW) return "";
  return RAW.endsWith("/") ? RAW.slice(0, -1) : RAW;
}

/**
 * Build a URL into the platform.
 *
 * @param path   path within the platform app, e.g. "/ar/signup"
 */
export function platformUrl(path = "/"): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const root = base();
  return root ? `${root}${suffix}` : suffix;
}

/**
 * Whether the platform is actually reachable, as opposed to merely addressed.
 *
 * Separate from {@link isPlatformConfigured} on purpose. The subdomain can be
 * known long before anything answers on it — which is exactly the state the
 * launch was in when this was written, and a visitor clicking the primary call
 * to action got `DNS_PROBE_FINISHED_NXDOMAIN`.
 *
 * A broken primary CTA is worse than an honest "not yet": the first costs you
 * the visitor and tells you nothing, the second costs you nothing and can
 * capture their interest.
 *
 * Defaults to FALSE. A deployment must opt in by setting
 * `VITE_PLATFORM_LIVE=true`, so forgetting the flag leaves the site honest
 * rather than broken.
 */
export const isPlatformLive =
  String(import.meta.env.VITE_PLATFORM_LIVE ?? "")
    .trim()
    .toLowerCase() === "true";

/**
 * The console's desktop launcher, `/{locale}/start`.
 *
 * A decision node, not a destination: it asks the gateway what this person has
 * paid for and then tries to open THKY Desktop, offering the download page
 * either way.
 *
 * **It is no longer where authentication ends.** It was, and that was the
 * defect: every «جرّب ذكي» press ended with a signed-in customer looking at a
 * deep link to an app they had not installed, or at a plans grid, rather than
 * at their own workspace. Someone who wants the launcher can still reach it —
 * it is a link in the authenticated navigation — but nothing sends them there.
 *
 * Kept exported because it is the honest name for that URL and the tests
 * assert that the CTA does *not* point at it.
 *
 * The route is served by the console rather than this static site because the
 * gateway ships no CORS policy, so only a same-origin caller can ask the
 * entitlement question. See `docs/ATHKYA_ENTRY_FLOW.md`.
 */
export function startUrl(locale: string): string {
  return platformUrl(`/${locale}/start`);
}

/**
 * Where authentication ends: the console's post-sign-in resolver.
 *
 * `/{locale}/continue` asks one question — what should this person see now? —
 * and answers it with an invitation they were part-way through, first-use
 * setup, or the app home at `/{locale}/app`. This site does not and cannot make
 * that decision: it holds no session, and the gateway it would have to ask has
 * no CORS policy.
 *
 * Exported so a test can name it, and so the `returnTo` below has one source.
 */
export function continueUrl(locale: string): string {
  return platformUrl(`/${locale}/continue`);
}

/**
 * The console's canonical sign-in page — the one screen in the product that
 * offers email, registration and «المتابعة باستخدام Google» together.
 *
 * ── WHY EVERY ENTRY LINK COMES HERE ───────────────────────────────────────
 *
 * There is exactly one authentication surface, and it belongs to the console
 * (`clients/console/src/pages/Account.tsx`). This site never collects a
 * credential, never holds a session and never renders a second sign-in form.
 * It only decides *which* console URL a visitor should arrive at.
 *
 * ── WHY `returnTo` AND NOT A DIRECT LINK ──────────────────────────────────
 *
 * Sending a signed-out visitor straight to the destination is not a security
 * hole — the console bounces them back here — but it costs them a spinner and
 * two redirects before they see the form they needed all along. Carrying it as
 * `returnTo` inverts that: the form first, the decision immediately after, with
 * nothing skipped. `AuthRoute` in the console resumes it, and validates it
 * before it does.
 *
 * ── WHY `/continue` AND NOT `/start` ──────────────────────────────────────
 *
 * This carried `returnTo=/{locale}/start` until 2026-08-19, which meant every
 * entry link — «جرّب ذكي», «تسجيل الدخول», «إنشاء حساب», all of them — ended a
 * successful sign-in at the desktop launcher. A new customer's first sight of
 * the product they had just joined was a prompt to download something, or a
 * plans grid if they had not paid. `/continue` ends it in their workspace
 * instead, and routes to setup or a pending invitation when either applies.
 *
 * The console treats a `returnTo` of `/start` as "decide for me" for the same
 * reason, so links already in the wild recover rather than reproducing the old
 * behaviour.
 *
 * @param locale   the locale segment the visitor is already browsing in
 * @param mode     `"signin"` opens the sign-in form, `"signup"` the registration
 *                 form. Both screens are the same component and each offers the
 *                 other, so this only chooses which one is on top.
 */
export function authUrl(
  locale: string,
  mode: "signin" | "signup" = "signin",
): string {
  const page = mode === "signup" ? "signup" : "login";
  const returnTo = encodeURIComponent(`/${locale}/continue`);
  return platformUrl(`/${locale}/${page}?returnTo=${returnTo}`);
}

/**
 * Where the primary CTA should send someone right now.
 *
 * Until the platform is live this is a `mailto:` to the contact address — crude,
 * but it needs no backend, works from a static build today, and puts a real
 * interested person in your inbox instead of on an error page.
 *
 * Returns `null` when the platform is not live AND no contact address is
 * configured. There is then genuinely nowhere to send anyone, and the caller
 * renders a disabled state rather than a link that goes nowhere.
 */
export function primaryCtaTarget(
  locale: string,
  subject: string,
): { href: string; isWaitlist: boolean } | null {
  if (isPlatformLive) {
    return { href: authUrl(locale), isWaitlist: false };
  }
  if (!isContactConfigured) {
    return null;
  }
  return {
    href: `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}`,
    isWaitlist: true,
  };
}

/** The header's «تسجيل الدخول» link. Same page, opened on the sign-in form. */
export function signInUrl(locale: string): string {
  return authUrl(locale, "signin");
}

/** «إنشاء حساب». Same page, opened on the registration form. */
export function signUpUrl(locale: string): string {
  return authUrl(locale, "signup");
}

/**
 * The contact address, from `VITE_CONTACT_EMAIL` at build time.
 *
 * No domain is hardcoded, for the same reason the platform URL is not.
 *
 * The old fallback was `hello@athkya.example` — a reserved TLD, so it can never
 * receive mail. That is worse than an obvious gap: the site would ship looking
 * complete while every message a visitor sent bounced, and nobody would find out
 * until someone complained they had been ignored. `isContactConfigured` lets the
 * UI say plainly that no address is set instead.
 */
const RAW_CONTACT = import.meta.env.VITE_CONTACT_EMAIL?.trim();

/** True when the deployment has been given a real contact address. */
export const isContactConfigured = Boolean(RAW_CONTACT);

export const contactEmail = RAW_CONTACT ?? "";
